import { type FormEvent, type PropsWithChildren, type ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ModalProps extends PropsWithChildren {
  open: boolean;
  title: string;
  description?: string;
  eyebrow?: string;
  submitLabel?: string;
  submitIcon?: ReactNode;
  onClose: () => void;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  size?: 'md' | 'lg';
  submitDisabled?: boolean;
  className?: string;
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  confirmIcon?: ReactNode;
  variant?: 'primary' | 'danger';
  pending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function Modal({ open, title, description, eyebrow, submitLabel, submitIcon, onClose, onSubmit, size = 'md', submitDisabled = false, className = '', children }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;
  return <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <form className={`dialog form-dialog form-dialog--${size} ${className}`} role="dialog" aria-modal="true" aria-labelledby="form-dialog-title" onSubmit={onSubmit ?? ((event) => event.preventDefault())}>
      <header><div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h2 id="form-dialog-title">{title}</h2>{description && <p>{description}</p>}</div><button type="button" className="icon-button" onClick={onClose} aria-label="Close dialog"><X /></button></header>
      <div className="dialog-body">{children}</div>
      <footer>{onSubmit ? <><Button type="button" variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" disabled={submitDisabled}>{submitIcon}{submitLabel}</Button></> : <Button type="button" variant="secondary" onClick={onClose}>Close</Button>}</footer>
    </form>
  </div>;
}

export function ConfirmDialog({ open, title, description, confirmLabel = 'Confirm', confirmIcon, variant = 'primary', pending = false, onClose, onConfirm }: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && !pending && onClose();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, pending, onClose]);

  if (!open) return null;
  return <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !pending && onClose()}>
    <section className="dialog confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-description">
      <header><div><h2 id="confirm-dialog-title">{title}</h2></div><button type="button" className="icon-button" onClick={onClose} disabled={pending} aria-label="Close confirmation"><X /></button></header>
      <div className="dialog-body confirm-dialog__body">{confirmIcon && <span className={`confirm-dialog__icon confirm-dialog__icon--${variant}`}>{confirmIcon}</span>}<p id="confirm-dialog-description">{description}</p></div>
      <footer><Button type="button" variant="secondary" onClick={onClose} disabled={pending}>Cancel</Button><Button type="button" variant={variant} onClick={onConfirm} disabled={pending}>{confirmIcon}{pending ? 'Please wait…' : confirmLabel}</Button></footer>
    </section>
  </div>;
}

export function DialogField({ label, required, hint, children, className = '' }: PropsWithChildren<{ label: string; required?: boolean; hint?: string; className?: string }>) {
  return <label className={`dialog-field ${className}`}><span>{label}{required && <b>*</b>}</span>{children}{hint && <small>{hint}</small>}</label>;
}
